<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  products: {
    type: Array,
    required: true,
  },
  catalogId: {
    type: String,
    required: true,
  },
  messageType: {
    type: String,
    default: 'product',
  },
});

const emit = defineEmits(['back', 'send']);

const { t } = useI18n();

const bodyText = ref('');
const footerText = ref('');
const headerText = ref('');

const isCatalogMessage = computed(
  () => props.messageType === 'catalog_message'
);
const isSingleProduct = computed(
  () => !isCatalogMessage.value && props.products.length === 1
);

const payload = computed(() => {
  if (isCatalogMessage.value) {
    const body = bodyText.value || t('CATALOG_PRODUCTS.CATALOG_DEFAULT_BODY');
    const interactive = {
      type: 'catalog_message',
      body: { text: body },
      action: { name: 'catalog_message' },
    };
    if (footerText.value) {
      interactive.footer = { text: footerText.value };
    }

    return {
      message: body,
      contentAttributes: {
        type: 'interactive',
        interactive,
        catalog_id: props.catalogId,
      },
    };
  }

  const productMeta = props.products.map(p => ({
    name: p.name,
    retailer_id: p.retailer_id,
    price: p.price,
    image_url: p.image_url,
    currency: p.currency,
  }));

  if (isSingleProduct.value) {
    return {
      message: bodyText.value || props.products[0].name,
      contentAttributes: {
        product_info: {
          type: 'product',
          catalog_id: props.catalogId,
          product_retailer_id: props.products[0].retailer_id,
          body: bodyText.value || undefined,
          footer: footerText.value || undefined,
          products: productMeta,
        },
      },
    };
  }

  return {
    message: headerText.value || bodyText.value || 'Products',
    contentAttributes: {
      product_info: {
        type: 'product_list',
        catalog_id: props.catalogId,
        header: headerText.value || 'Products',
        body: bodyText.value || 'Check out these products',
        footer: footerText.value || undefined,
        sections: [
          {
            title: 'Products',
            product_items: props.products.map(p => ({
              product_retailer_id: p.retailer_id,
            })),
          },
        ],
        products: productMeta,
      },
    },
  };
});

const onSend = () => {
  emit('send', payload.value);
};
</script>

<template>
  <div class="w-full">
    <!-- Back button -->
    <button
      class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 mb-4"
      @click="$emit('back')"
    >
      <Icon icon="i-lucide-arrow-left" class="size-4" />
      {{ t('CATALOG_PRODUCTS.CANCEL') }}
    </button>

    <!-- Selected products preview -->
    <div v-if="isCatalogMessage" class="mb-4 border-y border-n-weak py-4">
      <div class="flex items-center gap-3">
        <span class="i-lucide-store size-5 text-n-slate-10" />
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CATALOG_PRODUCTS.CATALOG_MESSAGE') }}
          </p>
          <p class="text-xs text-n-slate-11 break-all">
            {{ catalogId }}
          </p>
        </div>
      </div>
    </div>
    <div v-else class="mb-4 space-y-2">
      <div
        v-for="product in products"
        :key="product.retailer_id"
        class="flex gap-3 p-3 rounded-lg border border-n-weak bg-n-background"
      >
        <div
          class="shrink-0 w-12 h-12 rounded-lg bg-n-slate-3 overflow-hidden flex items-center justify-center"
        >
          <img
            v-if="product.image_url"
            :src="product.image_url"
            :alt="product.name"
            class="w-full h-full object-cover"
          />
          <Icon v-else icon="i-lucide-package" class="size-5 text-n-slate-10" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ product.name }}
          </p>
          <p v-if="product.price" class="text-xs text-n-slate-11">
            {{ product.price }}
          </p>
        </div>
      </div>
    </div>

    <!-- Header (for MPM only) -->
    <div v-if="!isSingleProduct && !isCatalogMessage" class="mb-3">
      <input
        v-model="headerText"
        type="text"
        :placeholder="t('CATALOG_PRODUCTS.HEADER_PLACEHOLDER')"
        maxlength="60"
        class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
      />
    </div>

    <!-- Body text -->
    <div class="mb-3">
      <textarea
        v-model="bodyText"
        :placeholder="t('CATALOG_PRODUCTS.BODY_PLACEHOLDER')"
        rows="2"
        maxlength="1024"
        class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand resize-none"
      />
    </div>

    <!-- Footer text -->
    <div class="mb-4">
      <input
        v-model="footerText"
        type="text"
        :placeholder="t('CATALOG_PRODUCTS.FOOTER_PLACEHOLDER')"
        maxlength="60"
        class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
      />
    </div>

    <!-- Send button -->
    <div class="flex justify-end">
      <button
        class="px-6 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90"
        @click="onSend"
      >
        {{ t('CATALOG_PRODUCTS.SEND') }}
      </button>
    </div>
  </div>
</template>
